import { FilterHelper } from "src/helpers";
import {
  DateTimeRange,
  PurchaseOrderPaginationOptions,
  PurchaseOrdersFilters,
  TransferOrderPaginationOptions,
  TransferOrdersFilters,
  ActiveAntsPurchaseOrderPaginationOptions,
  ActiveAntsPurchaseOrdersFilters,
  TransitPurchaseOrderPaginationOptions,
  TransitPurchaseOrdersFilters,
  UserPaginationOptions,
  UsersFiltersState,
  VendorPaginationOptions,
  VendorsFiltersState,
  WarehouseCheckInsFilters,
  WarehouseCheckInsPaginationOptions,
  ClaimPaginationOptions,
  ClaimsFilters
} from "src/types";
import {
  GetPurchaseOrdersQueryParams,
  GetPurchaseOrdersByBarcodeQueryParams,
  GetPurchaseOrdersForEuRelabelQueryParams,
  GetPurchaseOrdersForRelabelCaseBuilderQueryParams,
  GetTransferOrdersQueryParams,
  GetActiveAntsPurchaseOrdersQueryParams,
  GetTransitPurchaseOrdersQueryParams,
  GetVendorsQueryParams,
  GetWarehouseCheckInsQueryParams,
  GetUsersQueryParams,
  GetClaimsQueryParams,
  GetClaimsStatsQueryParams
} from "src/types/api";

const formatDateOnlyString = (date?: Date) => {
  if (!date) return undefined;
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export class QueryParamsMapper {
  static getWarehouseCheckInsQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: WarehouseCheckInsFilters,
    paginationOptions: WarehouseCheckInsPaginationOptions = {}
  ): GetWarehouseCheckInsQueryParams {
    const timestampStart = dateTimeRange?.start?.toISOString();
    const timestampEnd = dateTimeRange?.end?.toISOString();

    const type = FilterHelper.normaliseWarehouseCheckInTypeFilter(filters?.checkInTypeFilter);
    const status = filters?.checkInStatusFilter;
    const flaggedIssue = filters?.checkInFlaggedIssueFilter;

    const normalisedDateTimeRange = { timestampStart, timestampEnd };
    const normalisedFilters = {
      type: type,
      status: status,
      flaggedIssue: flaggedIssue,
      origin: filters?.origin,
      vendorName: filters?.vendorName,
      checkInUserEmail: filters?.checkInUserEmail,
      orderSenderEmail: filters?.orderSenderEmail,
      hasImages: filters?.hasImages
    };

    const normalisedPaginationOptions = { ...paginationOptions, searchTerm: paginationOptions.searchTerm };

    const queryParams: GetWarehouseCheckInsQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedFilters,
      ...normalisedPaginationOptions
    };

    return queryParams;
  }

  static getPurchaseOrdersQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: PurchaseOrdersFilters,
    paginationOptions: PurchaseOrderPaginationOptions = {},
    deliveryDateTimeRange?: DateTimeRange,
    receiptDateTimeRange?: DateTimeRange
  ): GetPurchaseOrdersQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();
    const deliveryDateStart = formatDateOnlyString(deliveryDateTimeRange?.start);
    const deliveryDateEnd = formatDateOnlyString(deliveryDateTimeRange?.end);
    const receiptDateStart = formatDateOnlyString(receiptDateTimeRange?.start);
    const receiptDateEnd = formatDateOnlyString(receiptDateTimeRange?.end);

    const completionStatus = FilterHelper.normalisePOCompletionStatusFilter(filters?.completionStatusFilter);
    const confirmationStatus = FilterHelper.normalisePOConfirmationStatusFilter(filters?.confirmationStatusFilter);
    const checkInStatus = filters?.checkInStatusFilter;
    const checkInFlaggedIssue = filters?.checkInFlaggedIssueFilter;

    const hasGxoError = filters?.confirmationStatusFilter === "GXO Error" ? true : undefined;
    const portalAccess =
      filters?.confirmationStatusFilter === "TBC (Non-Portal)" ||
      filters?.confirmationStatusFilter === "Overdue (Non-Portal)"
        ? false
        : undefined;

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedDeliveryDateTimeRange = { deliveryDateStart, deliveryDateEnd };
    const normalisedReceiptDateTimeRange = { receiptDateStart, receiptDateEnd };
    const normalisedFilters = {
      completionStatus: completionStatus,
      checkInFlaggedIssue: checkInFlaggedIssue,
      checkInStatus: checkInStatus,
      confirmationStatus: confirmationStatus,
      origin: filters?.origin,
      vendorName: filters?.vendorName,
      senderEmail: filters?.senderEmail,
      hasTrackingNo: filters?.hasTrackingNo,
      hasInvoice: filters?.hasInvoice,
      isOverdueDelivery: filters?.isOverdueDelivery,
      isInvoicePaymentDue: filters?.isInvoicePaymentDue,
      hasGxoError: hasGxoError,
      portalAccess: portalAccess,
      poRelationshipType: filters?.poRelationshipType
    };

    const normalisedPaginationOptions = { ...paginationOptions, searchTerm: paginationOptions.searchTerm };

    const queryParams: GetPurchaseOrdersQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedDeliveryDateTimeRange,
      ...normalisedReceiptDateTimeRange,
      ...normalisedFilters,
      ...normalisedPaginationOptions
    };

    return queryParams;
  }

  static getPurchaseOrdersByBarcodeQueryParams(
    dateTimeRange?: DateTimeRange,
    paginationOptions: PurchaseOrderPaginationOptions = {}
  ): GetPurchaseOrdersByBarcodeQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedPaginationOptions = { ...paginationOptions, searchTerm: paginationOptions.searchTerm };

    const queryParams: GetPurchaseOrdersByBarcodeQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedPaginationOptions
    };

    return queryParams;
  }

  static getPurchaseOrdersForEuRelabelQueryParams(
    paginationOptions: PurchaseOrderPaginationOptions = {}
  ): GetPurchaseOrdersForEuRelabelQueryParams {
    const trimmed = paginationOptions.searchTerm?.trim();
    return {
      ...paginationOptions,
      searchTerm: trimmed ? trimmed : undefined
    };
  }

  static getPurchaseOrdersForRelabelCaseBuilderQueryParams(
    paginationOptions: PurchaseOrderPaginationOptions = {}
  ): GetPurchaseOrdersForRelabelCaseBuilderQueryParams {
    const trimmed = paginationOptions.searchTerm?.trim();
    return {
      ...paginationOptions,
      searchTerm: trimmed ? trimmed : undefined
    };
  }

  static getUsersQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: UsersFiltersState,
    paginationOptions: UserPaginationOptions = {}
  ): GetUsersQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const trimmedJobTitle = filters?.jobTitle?.trim() || undefined;
    const trimmedSupervisorEmail = filters?.supervisorEmail?.trim() || undefined;
    const trimmedVendorName = filters?.vendorName?.trim() || undefined;

    const queryParams: GetUsersQueryParams = {
      createdAtStart,
      createdAtEnd,
      role: filters?.roles.length ? filters.roles : undefined,
      status: filters?.statuses.length ? filters.statuses : undefined,
      vendorName: trimmedVendorName,
      jobTitle: trimmedJobTitle,
      supervisorEmail: trimmedSupervisorEmail,
      ...paginationOptions
    };

    return queryParams;
  }

  static getVendorsQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: VendorsFiltersState,
    paginationOptions: VendorPaginationOptions = {}
  ): GetVendorsQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const trimmedVendorName = filters?.vendorName?.trim() || undefined;
    const trimmedLocation = filters?.location?.trim() || undefined;
    const trimmedCreatedBy = filters?.createdByUserEmail?.trim() || undefined;
    const trimmedEmails = filters?.emails?.trim() || undefined;

    const queryParams: GetVendorsQueryParams = {
      createdAtStart,
      createdAtEnd,
      vendorName: trimmedVendorName,
      location: trimmedLocation,
      createdByUserEmail: trimmedCreatedBy,
      portalOrderVendor: filters?.portalOrderVendor,
      collectionOrderVendor: filters?.collectionOrderVendor,
      portalAccess: filters?.portalAccess,
      isProForma: filters?.isProForma,
      fileType: filters?.fileTypes.length ? filters.fileTypes : undefined,
      emails: trimmedEmails,
      ...paginationOptions
    };

    return queryParams;
  }

  static getTransitPurchaseOrdersQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: TransitPurchaseOrdersFilters,
    paginationOptions: TransitPurchaseOrderPaginationOptions = {}
  ): GetTransitPurchaseOrdersQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const transitStatus = FilterHelper.normaliseTransitStatusFilter(filters?.transitStatusFilter);

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedFilters = {
      transitStatus: transitStatus,
      vendorName: filters?.vendorName,
      senderEmail: filters?.senderEmail,
      matchStatus: filters?.matchStatusFilter,
      action: filters?.actionFilter,
      completionStatus: filters?.completionStatusFilter,
      isBackorder: filters?.isBackorderFilter
    };

    const normalisedPaginationOptions = { ...paginationOptions, searchTerm: paginationOptions.searchTerm };

    const queryParams: GetTransitPurchaseOrdersQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedFilters,
      ...normalisedPaginationOptions
    };

    return queryParams;
  }

  static getClaimsQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: ClaimsFilters,
    paginationOptions: ClaimPaginationOptions = {}
  ): GetClaimsQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const status = FilterHelper.normaliseClaimStatusFilter(filters?.statusFilter);

    // Normalize claimType to array format
    const claimType = filters?.claimType
      ? Array.isArray(filters.claimType)
        ? filters.claimType
        : [filters.claimType]
      : undefined;

    // Normalize issueType to array format
    const issueType = filters?.issueType
      ? Array.isArray(filters.issueType)
        ? filters.issueType
        : [filters.issueType]
      : undefined;

    // Normalize urgencyLevel to array format
    const urgencyLevel = filters?.urgencyLevel
      ? Array.isArray(filters.urgencyLevel)
        ? filters.urgencyLevel
        : [filters.urgencyLevel]
      : undefined;

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedFilters = {
      status: status,
      vendorName: filters?.vendorName,
      submittedByEmail: filters?.submittedByEmail,
      senderEmail: filters?.senderEmail,
      claimType: claimType,
      issueType: issueType,
      urgencyLevel: urgencyLevel,
      warehouseLocation: filters?.warehouseLocation
    };

    const normalisedPaginationOptions = { ...paginationOptions, searchTerm: paginationOptions.searchTerm };

    const queryParams: GetClaimsQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedFilters,
      ...normalisedPaginationOptions
    };

    return queryParams;
  }

  static getTransferOrdersQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: TransferOrdersFilters,
    paginationOptions: TransferOrderPaginationOptions = {}
  ): GetTransferOrdersQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const completionStatus = filters?.completionStatusFilter;

    const queryParams: GetTransferOrdersQueryParams = {
      createdAtStart,
      createdAtEnd,
      completionStatus,
      ...paginationOptions,
      searchTerm: paginationOptions.searchTerm
    };

    return queryParams;
  }

  static getActiveAntsPurchaseOrdersQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: ActiveAntsPurchaseOrdersFilters,
    paginationOptions: ActiveAntsPurchaseOrderPaginationOptions = {}
  ): GetActiveAntsPurchaseOrdersQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const status = filters?.statusFilter;

    const queryParams: GetActiveAntsPurchaseOrdersQueryParams = {
      createdAtStart,
      createdAtEnd,
      status,
      ...paginationOptions,
      searchTerm: paginationOptions.searchTerm
    };

    return queryParams;
  }

  static getClaimsStatsQueryParams(
    dateTimeRange?: DateTimeRange,
    filters?: ClaimsFilters,
    searchTerm?: string
  ): GetClaimsStatsQueryParams {
    const createdAtStart = dateTimeRange?.start?.toISOString();
    const createdAtEnd = dateTimeRange?.end?.toISOString();

    const status = FilterHelper.normaliseClaimStatusFilter(filters?.statusFilter);

    // Normalize claimType to array format
    const claimType = filters?.claimType
      ? Array.isArray(filters.claimType)
        ? filters.claimType
        : [filters.claimType]
      : undefined;

    // Normalize issueType to array format
    const issueType = filters?.issueType
      ? Array.isArray(filters.issueType)
        ? filters.issueType
        : [filters.issueType]
      : undefined;

    // Normalize urgencyLevel to array format
    const urgencyLevel = filters?.urgencyLevel
      ? Array.isArray(filters.urgencyLevel)
        ? filters.urgencyLevel
        : [filters.urgencyLevel]
      : undefined;

    const normalisedDateTimeRange = { createdAtStart, createdAtEnd };
    const normalisedFilters = {
      status: status,
      vendorName: filters?.vendorName,
      submittedByEmail: filters?.submittedByEmail,
      senderEmail: filters?.senderEmail,
      claimType: claimType,
      issueType: issueType,
      urgencyLevel: urgencyLevel,
      warehouseLocation: filters?.warehouseLocation
    };

    const queryParams: GetClaimsStatsQueryParams = {
      ...normalisedDateTimeRange,
      ...normalisedFilters,
      searchTerm: searchTerm
    };

    return queryParams;
  }
}
